import React, {MouseEvent, useEffect, useState} from 'react';
import Box from '@mui/material/Box';
import {
  DataGridPro,
  DataGridProProps,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
  GridSortCellParams,
  GridValueGetterParams,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import {ErrorData, Player} from 'app/components/team/playerstats/types';
import {COLORS, Image, Typography} from '@fupa/fupa-uikit';
import styled from 'styled-components';
import NoRowsOverlay from 'app/components/team/playerstats/NoRowsOverlay';
import {useHistory} from 'react-router';
import {FPHTMLLink, FPLink} from 'app/components/links/link';

const PlayerWrapper = styled(FPHTMLLink)`
  display: flex;
  align-items: center;
  height: inherit;
  text-decoration: none;
  overflow: hidden;
`;

const PlayerLink = styled(FPLink)`
  display: flex;
  align-items: center;
  height: inherit;
  text-decoration: none;
  overflow: hidden;
`;

const ImageWrapper = styled.div`
  margin-right: 8px;
`;

const StyledTypography = styled(Typography)`
  &&& {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal;
  }
`;

// augment the props for the toolbar slot
declare module '@mui/x-data-grid-pro' {
  interface NoRowsOverlayPropsOverrides {
    error: ErrorData;
  }
}

const renderPlayer = (props: GridRenderCellParams<Player>) => {
  const {row} = props;
  const playerContent = (
    <>
      <ImageWrapper>
        <Image entity='player' height={32} width={32} src={row.image} circle={true} ratio='1:1' />
      </ImageWrapper>
      <StyledTypography variant='body1'>{`${row.firstName || ''} ${row.lastName || ''}`}</StyledTypography>
    </>
  );
  if (row.slug) {
    return (
      <PlayerLink to={`/player/${row.slug}`} onClick={(e: MouseEvent) => e.stopPropagation()}>
        {playerContent}
      </PlayerLink>
    );
  }
  return <PlayerWrapper>{playerContent}</PlayerWrapper>;
};

const statGridData: Partial<GridColDef> = {
  align: 'center',
  type: 'number',
};

const defaultColumns: GridColDef[] = [
  {
    field: 'rank',
    headerName: 'Pl.',
    valueGetter: (params: GridValueGetterParams) => {
      if (params.value) {
        return `${params.value}.`;
      }
      const sortedRows = params.api.getSortedRows();
      const index = sortedRows.findIndex(row => row.slug === params.row.slug);
      return `${index + 1}.`;
    },
    sortable: false,
    minWidth: 42,
    width: 42,
  },
  {
    field: 'player',
    headerName: 'Spieler',
    renderCell: renderPlayer,
    sortable: false,
    width: 136,
  },
  {
    field: 'matches',
    headerName: 'Spiele',
    ...statGridData,
    width: 86,
  },
  {
    field: 'goals',
    headerName: 'Tore',
    ...statGridData,
    width: 74,
  },
  {
    field: 'assists',
    headerName: 'Assists',
    ...statGridData,
    width: 89,
  },
  {
    field: 'penalties',
    headerName: '11m',
    valueGetter: ({row}) => `${row.penaltiesHit}/${row.penaltiesTotal}`,
    sortComparator: (_v1, _v2, param1: GridSortCellParams<Player>, param2: GridSortCellParams<Player>) => {
      const row1 = param1.api.getRow(param1.id);
      const row2 = param2.api.getRow(param2.id);
      return row1.penaltiesHit - row2.penaltiesHit;
    },
    ...statGridData,
    description: 'Elfmeter verwandelt/Elfmeter gesamt',
    width: 76,
  },
  {
    field: 'yellowCards',
    headerName: 'Gelb',
    ...statGridData,
    width: 76,
  },
  {
    field: 'yellowRedCards',
    headerName: 'Gelb-Rot',
    ...statGridData,
    width: 81,
  },
  {
    field: 'redCards',
    headerName: 'Rot',
    ...statGridData,
    width: 70,
  },
  {
    field: 'substitutesIn',
    headerName: 'Ein.',
    ...statGridData,
    description: 'Einwechslungen',
    width: 70,
  },
  {
    field: 'substitutesOut',
    headerName: 'Aus.',
    ...statGridData,
    description: 'Auswechslungen',
    width: 73,
  },
  {
    field: 'minutesPlayed',
    headerName: 'Min.',
    ...statGridData,
    description: 'gespielte Minuten',
    width: 74,
  },
];

const topElevenColumn: GridColDef = {
  field: 'topEleven',
  headerName: 'Elf d. Woche',
  ...statGridData,
  description: 'Anzahl der Nominierungen in die Elf der Woche',
  width: 88,
};

interface PlayerStatsGridProps {
  type: string;
  players: Player[];
  isLoading: boolean;
  error: ErrorData;
  calcMaxHeight: () => '100%' | number;
  withTopEleven: boolean;
}

type PlayerStatsAndGridProps = PlayerStatsGridProps & Partial<DataGridProProps>;

export default function PlayerStatsGrid({
  type,
  players,
  isLoading,
  error,
  calcMaxHeight,
  withTopEleven,
  ...props
}: PlayerStatsAndGridProps) {
  const history = useHistory();
  const [maxHeight, setMaxHeight] = useState<'100%' | number>('100%');
  const apiRef = useGridApiRef();

  useEffect(() => {
    const maxHeight = calcMaxHeight();
    setMaxHeight(maxHeight);
  }, [isLoading, players, error]);

  useEffect(() => {
    const handleResize = () => {
      const maxHeight = calcMaxHeight();
      setMaxHeight(maxHeight);
      if (typeof apiRef.current?.forceUpdate === 'function') {
        apiRef.current?.forceUpdate();
      }
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [isLoading, players, error]);

  const handleRowClick = (params: GridRowParams<Player>): void => {
    const slug = params.row.slug;
    if (slug) {
      history.push(`/player/${slug}`);
    }
  };

  return (
    <Box sx={{height: maxHeight, width: '100%'}}>
      <DataGridPro
        key={type}
        columns={[...defaultColumns, ...(withTopEleven ? [topElevenColumn] : [])]}
        rows={players}
        initialState={{
          pinnedColumns: {left: ['rank', 'player']},
          sorting: {
            sortModel: [{field: 'matches', sort: 'desc'}],
          },
        }}
        sortingOrder={['desc', 'asc', null]}
        disableRowSelectionOnClick={true}
        disableColumnMenu={true}
        disableColumnReorder={true}
        disableVirtualization={true}
        disableColumnResize={true}
        loading={isLoading}
        slots={{
          noRowsOverlay: NoRowsOverlay,
        }}
        slotProps={{
          noRowsOverlay: {error},
        }}
        onRowClick={handleRowClick}
        sx={{
          '&.MuiDataGrid-root': {border: 'none'},
          '&.MuiDataGrid-root .MuiDataGrid-iconButtonContainer': {width: '28px'},
          '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': {py: '6px', outline: 'none', cursor: 'pointer'},
          '&.MuiDataGrid-root .MuiDataGrid-cell[data-field="player"], &.MuiDataGrid-root .MuiDataGrid-cell[data-field="rank"], &.MuiDataGrid-root .MuiDataGrid-columnHeader[data-field="player"], &.MuiDataGrid-root .MuiDataGrid-columnHeader[data-field="rank"]':
            {px: '8px'},
          '&.MuiDataGrid-root .MuiDataGrid-columnHeader': {outline: 'none'},
          '&.MuiDataGrid-root .MuiDataGrid-row:hover, &.MuiDataGrid-root .MuiDataGrid-row.Mui-hovered': {
            backgroundColor: COLORS.lightestGrey,
          },
          '&.MuiDataGrid-root .MuiDataGrid-columnHeaderTitleContainer': {
            flexDirection: 'row',
          },
          '&.MuiDataGrid-root .MuiDataGrid-columnHeaderTitle': {
            whiteSpace: 'normal',
            lineHeight: 'normal',
            overflow: 'visible',
          },
          '&.MuiDataGrid-root .MuiDataGrid-columnSeparator': {display: 'none'},
        }}
        {...props}
      />
    </Box>
  );
}
